import {
    AppUser,
    IOptionsWithTypes,
    isUserPartOfUsersWithTags,
    spErrorTags,
    spLog,
    spLogTags,
    TOrgTag,
} from '@eventr/common'

import { drawerStore } from '@eventr/common-storybook'
import { createSelector } from 'reselect'
import { StateCreator } from 'zustand'
import { UserAPI } from '../data/User'
import { storeClaims } from '../feat/oauth/oauth.slice'
import { BoundStore, bs } from './bound.store'
import { selectFilterNonItineraryUsers } from './itineraryState/currentItin.slice'
import { nameFormats } from './NameFormats'

interface UserState {
    override: number | null
    experimental: boolean
    users: AppUser[]
    wpUser: AppUser | undefined
    isGuest: boolean
    userClaims?: any
    tier?: string
}

interface UserActions {
    setOverride: (id: number | null, clearOverlay?: boolean) => void
    setExperimental: (id: boolean) => void
    setWpUser: (user: AppUser) => void
    setWpUserPartial: (user: Partial<AppUser>) => void
    getCurrentUser: () => AppUser | undefined
    isUserPartOfSelection: (selection: IOptionsWithTypes[]) => boolean
    getUserFromID: (id: number) => AppUser | undefined
    checkAdmin: () => boolean
    getUserFromName: (name: string) => AppUser | undefined
    getUsersWithTags: (deptTags: string[], groupTags: string[]) => AppUser[]
    fetchUsers: () => Promise<void>
    getNameFormatted: (user: AppUser, format?: number) => string
    /**
     * @deprecated use selectItineraryUsers instead
     */
    selectedItineraryUsers: () => AppUser[]
    selectedItineraryGroupsAndDepartments: () => TOrgTag[]
    selectedItineraryUsersOnlyFromGroupsAndDepartments: (
        userToUse?: AppUser[]
    ) => AppUser[]
}
export interface IUserSlice extends UserState, UserActions {}
export const createUserSlice: StateCreator<
    BoundStore,
    [['zustand/devtools', never]],
    [],
    IUserSlice
> = (set, get) => ({
    override: null,
    users: [],
    isGuest: false,
    setOverride: (id: number | null, clearOverlay: boolean = true) => {
        if (clearOverlay) {
            drawerStore().closeDrawer()
        }
        //prevent setting override to null if user is a guest
        if (get().wpUser?.roles.includes('guest')) {
            set(
                { override: (get().wpUser as any).followUserId },
                false,
                'setOverride'
            )
            return
        }
        set({ override: id }, false, 'setOverride')
    },
    experimental: false,
    setExperimental: (bool: boolean) => {
        set({ experimental: bool }, false, 'setExperimental')
    },

    wpUser: undefined,
    setWpUser: (user: AppUser) => {
        // if user is a guest, set override to follow user prop
        if (user?.roles?.includes('guest')) {
            set(
                {
                    override: (user as any).followUserId,
                    isGuest: true,
                },
                false,
                'setWpUser'
            )
        } else {
            set({ isGuest: false, override: null }, false, 'setWpUser')
        }

        set({ wpUser: user }, false, 'setWpUser')
    },
    setWpUserPartial: (user: Partial<AppUser>) => {
        const current = get().wpUser
        if (!current) {
            return
        }
        const newUser = { ...current, ...user }
        get().setWpUser(newUser)
    },

    isUserPartOfSelection: (selection: IOptionsWithTypes[]) => {
        const user = get().getCurrentUser()
        if (!selection) return false
        if (selection.length < 1) return false
        try {
            return user ? isUserPartOfUsersWithTags(user, selection) : false
        } catch {
            return false
        }
    },
    getUserFromID: (id: number) => {
        if (!id) {
            return undefined
        }
        return get().users.find((user: AppUser) => {
            return user.id === id
        })
    },
    getCurrentUser: () => {
        const id = get().override ?? get().wpUser?.id
        return id !== undefined ? get().getUserFromID(id) : undefined
    },
    checkAdmin: () => {
        const user = get().wpUser
        if (
            user &&
            (user.roles.includes('eventr_manager') ||
                user.roles.includes('administrator') ||
                user.roles.includes('owner'))
        ) {
            return true
        }
        return false
    },
    getUserFromName: (name: string) => {
        return get().users.find((user: AppUser) => {
            return user.firstName + ' ' + user.lastName === name
        })
    },
    getUsersWithTags: (deptTags: string[], groupTags: string[]) => {
        var foundUsers: AppUser[] = []

        for (const user of get().users) {
            if (user.id !== 1) {
                if (user.departmentTags && user.departmentTags.length > 0) {
                    for (const tag of user.departmentTags) {
                        if (deptTags.includes(tag)) {
                            if (!foundUsers.includes(user)) {
                                foundUsers.push(user)
                            }
                        }
                    }
                }
                if (user.groupTags && user.groupTags.length > 0) {
                    for (const tag of user.groupTags) {
                        if (groupTags.includes(tag)) {
                            if (!foundUsers.includes(user)) {
                                foundUsers.push(user)
                            }
                        }
                    }
                }
            }
        }

        return foundUsers
    },
    fetchUsers: async () => {
        try {
            let usersRes = await UserAPI.getUsers()
            const me = await UserAPI.getCurrentUser()
            if (me.status === 'success') {
                if (me.data?.deletedUnix) {
                    set({ wpUser: undefined }, false, 'imDeleted')
                    return
                }
                set({ wpUser: me.data }, false, 'setWpUser')
                storeClaims(me.data?.claimsJWT)
            }
            usersRes = usersRes.filter((user: AppUser) => {
                return userFilterFunc(user)
            })
            set({ users: usersRes }, false, 'seUsers')
        } catch (error: any) {
            if (error?.code === 'ERR_CANCELED') return
            spErrorTags('Failed to fetch users', ['USERS'], error)
        }
    },
    getNameFormatted: (user: AppUser, format?: number) => {
        if (!user) {
            return ''
        }

        const orgConfig = get().orgConfig
        const rawFirstName = user ? user.firstName ?? '' : ''
        const rawLastName = user ? user.lastName ?? '' : ''
        const firstnameInitial = rawFirstName.charAt(0).toUpperCase()
        const lastnameInitial = rawLastName.charAt(0).toUpperCase()
        const capitalizedFirstName = firstnameInitial + rawFirstName.slice(1)
        const capitalizedLastName = lastnameInitial + rawLastName.slice(1)

        if ((user && !user.firstName) || (user && !user.lastName)) {
            return user.email
        }

        let nameFormat = 1

        if (orgConfig && orgConfig.usernameAppFormat) {
            nameFormat = orgConfig.usernameAppFormat
        }

        if (format) {
            nameFormat = format
        }

        switch (nameFormat) {
            case nameFormats.firstname_lastname:
                return capitalizedFirstName + ' ' + capitalizedLastName
            case nameFormats.firstname_lastname_uppercase:
                return (
                    capitalizedFirstName +
                    ' ' +
                    capitalizedLastName
                ).toUpperCase()
            case nameFormats.firstname_lastname_lowercase:
                return (
                    capitalizedFirstName +
                    ' ' +
                    capitalizedLastName
                ).toLowerCase()
            case nameFormats.firstname_lastname_firstname_uppercase:
                return (
                    capitalizedFirstName.toUpperCase() +
                    ' ' +
                    capitalizedLastName
                )
            case nameFormats.firstname_lastname_lastname_uppercase:
                return (
                    capitalizedFirstName +
                    ' ' +
                    capitalizedLastName.toUpperCase()
                )
            case nameFormats.lastname_firstname:
                return capitalizedLastName + ' ' + capitalizedFirstName
            case nameFormats.lastname_firstname_uppercase:
                return (
                    capitalizedLastName +
                    ' ' +
                    capitalizedFirstName
                ).toUpperCase()
            case nameFormats.lastname_firstname_lowercase:
                return (
                    capitalizedLastName +
                    ' ' +
                    capitalizedFirstName
                ).toLowerCase()
            case nameFormats.lastname_firstname_firstname_uppercase:
                return (
                    capitalizedLastName +
                    ' ' +
                    capitalizedFirstName.toUpperCase()
                )
            case nameFormats.lastname_firstname_lastname_uppercase:
                return (
                    capitalizedLastName.toUpperCase() +
                    ' ' +
                    capitalizedFirstName
                )
            case nameFormats.firstnameInitial_lastname:
                return firstnameInitial + ' ' + capitalizedLastName
            case nameFormats.firstnameInitial_lastname_uppercase:
                return (
                    firstnameInitial +
                    ' ' +
                    capitalizedLastName
                ).toUpperCase()
            case nameFormats.firstnameInitial_lastname_lowercase:
                return (
                    firstnameInitial +
                    ' ' +
                    capitalizedLastName
                ).toLowerCase()
            case nameFormats.firstnameInitial_lastname_firstname_uppercase:
                return (
                    firstnameInitial.toUpperCase() + ' ' + capitalizedLastName
                )
            case nameFormats.firstnameInitial_lastname_lastname_uppercase:
                return (
                    firstnameInitial + ' ' + capitalizedLastName.toUpperCase()
                )
            case nameFormats.firstname_lastnameInitial:
                return capitalizedFirstName + ' ' + lastnameInitial
            case nameFormats.firstname_lastnameInitial_uppercase:
                return (
                    capitalizedFirstName +
                    ' ' +
                    lastnameInitial
                ).toUpperCase()
            case nameFormats.firstname_lastnameInitial_lowercase:
                return (
                    capitalizedFirstName +
                    ' ' +
                    lastnameInitial
                ).toLowerCase()
            case nameFormats.firstname_lastnameInitial_firstname_uppercase:
                return (
                    capitalizedFirstName.toUpperCase() + ' ' + lastnameInitial
                )
            case nameFormats.firstname_lastnameInitial_lastname_uppercase:
                return (
                    capitalizedFirstName + ' ' + lastnameInitial.toUpperCase()
                )
            case nameFormats.lastnameInitial_firstname:
                return lastnameInitial + ' ' + capitalizedFirstName
            case nameFormats.lastnameInitial_firstname_uppercase:
                return (
                    lastnameInitial +
                    ' ' +
                    capitalizedFirstName
                ).toUpperCase()
            case nameFormats.lastnameInitial_firstname_lowercase:
                return (
                    lastnameInitial +
                    ' ' +
                    capitalizedFirstName
                ).toLowerCase()
            case nameFormats.lastnameInitial_firstname_firstname_uppercase:
                return (
                    lastnameInitial + ' ' + capitalizedFirstName.toUpperCase()
                )
            case nameFormats.lastnameInitial_firstname_lastname_uppercase:
                return (
                    lastnameInitial.toUpperCase() + ' ' + capitalizedFirstName
                )
            case nameFormats.firstnameInitial_lastnameInitial:
                return firstnameInitial + ' ' + lastnameInitial
            case nameFormats.firstnameInitial_lastnameInitial_uppercase:
                return (firstnameInitial + ' ' + lastnameInitial).toUpperCase()
            case nameFormats.firstnameInitial_lastnameInitial_lowercase:
                return (firstnameInitial + ' ' + lastnameInitial).toLowerCase()
            case nameFormats.firstnameInitial_lastnameInitial_firstname_lowercase:
                return firstnameInitial.toLowerCase() + ' ' + lastnameInitial
            case nameFormats.firstnameInitial_lastnameInitial_lastname_lowercase:
                return firstnameInitial + ' ' + lastnameInitial.toLowerCase()
            case nameFormats.firstname:
                return capitalizedFirstName
            case nameFormats.firstname_uppercase:
                return capitalizedFirstName.toUpperCase()
            case nameFormats.firstname_lowercase:
                return capitalizedFirstName.toLowerCase()
            case nameFormats.lastname:
                return capitalizedLastName
            case nameFormats.lastname_uppercase:
                return capitalizedLastName.toUpperCase()
            case nameFormats.lastname_lowercase:
                return capitalizedLastName.toLowerCase()
            default:
                return capitalizedFirstName + ' ' + capitalizedLastName
        }
    },
    /**
     * @deprecated use selectItineraryUsers instead
     */
    selectedItineraryUsers: () => {
        var itineraryUserIds: number[] = []

        const eventV2Data = get().getEventInfoV2()

        try {
            if (eventV2Data && eventV2Data.data && eventV2Data.data.members) {
                for (const member of eventV2Data.data.members) {
                    if (member.type === 'User') {
                        itineraryUserIds.push(member.option)
                    } else if (
                        member.type === 'Department' ||
                        member.type === 'Group'
                    ) {
                        const orgUserFromTag = get().users.filter((orgUser) => {
                            if (
                                (orgUser.departmentTags ?? []).includes(
                                    member.option
                                ) ||
                                (orgUser.groupTags ?? []).includes(
                                    member.option
                                )
                            ) {
                                return true
                            } else {
                                return false
                            }
                        })
                        if (orgUserFromTag) {
                            itineraryUserIds.push(
                                ...orgUserFromTag.map((u) => u.id)
                            )
                        }
                    }
                }
                return get().users.filter((user) => {
                    return itineraryUserIds.includes(user.id)
                })
            } else {
                /// No members list
                return []
            }
        } catch (error) {
            spLog('Failed to get itinerary users', error)
            return []
        }
    },
    selectedItineraryGroupsAndDepartments: () => {
        const orgConfig = get().orgConfig
        var groupsAndDepartments: TOrgTag[] = []
        try {
            const eventV2Data = get().getEventInfoV2()
            if (eventV2Data && eventV2Data.data && eventV2Data.data.members) {
                for (const member of eventV2Data.data.members) {
                    if (['DepartmentV2', 'Department'].includes(member.type)) {
                        const dep = orgConfig.departmentTags?.find((tag) => {
                            return tag.name === member.option
                        })
                        if (dep) groupsAndDepartments.push(dep)
                    }
                    if (['GroupV2', 'Group'].includes(member.type)) {
                        const gr = orgConfig.groupTags?.find((tag) => {
                            return tag.name === member.option
                        })
                        if (gr) groupsAndDepartments.push(gr)
                    }
                }
            }
        } catch (e) {
            spLogTags('Error', [
                'ERROR',
                'selectedItineraryGroupsAndDepartments',
            ])
        }

        return groupsAndDepartments
    },
    selectedItineraryUsersOnlyFromGroupsAndDepartments: (
        userToUse?: AppUser[]
    ) => {
        var foundUsers: AppUser[] = []
        var foundUsersIdList: number[] = []

        const selectedItineraryGroupsAndDepartmentsList =
            get().selectedItineraryGroupsAndDepartments()
        for (const orgUser of userToUse ?? get().users) {
            if (orgUser.id !== 1) {
                for (const itinTag of selectedItineraryGroupsAndDepartmentsList) {
                    if (
                        (orgUser.groupTags ?? []).includes(itinTag.name) ||
                        (orgUser.departmentTags ?? []).includes(itinTag.name)
                    ) {
                        if (!foundUsersIdList.includes(orgUser.id)) {
                            foundUsers.push(orgUser)
                            foundUsersIdList.push(orgUser.id)
                        }
                    }
                }
            }
        }

        return foundUsers
    },
})

/// To remove admin accounts and deleted ones
function userFilterFunc(user: AppUser) {
    if (user.roles.includes('administrator') || user.deletedUnix) {
        return
    } else {
        return user
    }
}

export const getUsersCommaSeparated = (
    users: IOptionsWithTypes[],
    onlyItinUsers?: boolean
) => {
    const filterNonItinUsers = onlyItinUsers ?? true
    if (!users || users.length < 1) {
        return ''
    }
    let onlyUsers = users.filter((user) => {
        return user.type === 'User'
    })
    if (onlyUsers.length < 1) {
        return ''
    }
    if (filterNonItinUsers) {
        onlyUsers = selectFilterNonItineraryUsers(bs(), onlyUsers)
    }
    const userNames = onlyUsers.map((user) => {
        const appUser = bs().getUserFromID(
            Number.parseInt(user.option?.toString())
        )

        if ((appUser?.deletedUnix ?? 0) > 0) {
            return ''
        }
        return appUser ? bs().getNameFormatted(appUser) : ''
    })
    return userNames.filter((s) => s !== '').join(', ')
}

//MARK: SELECTORS
export const selectItineraryUsers = createSelector(
    (state: BoundStore) => state.users,
    (state: BoundStore) => state.getEventInfoV2(),
    (users, eventInfo) => {
        var itineraryUserIds: number[] = []

        try {
            if (eventInfo && eventInfo.data && eventInfo.data.members) {
                for (const member of eventInfo.data.members) {
                    if (member.type === 'User') {
                        itineraryUserIds.push(member.option)
                    } else if (
                        member.type === 'Department' ||
                        member.type === 'Group'
                    ) {
                        const orgUserFromTag = users.filter((orgUser) => {
                            if (
                                (orgUser.departmentTags ?? []).includes(
                                    member.option
                                ) ||
                                (orgUser.groupTags ?? []).includes(
                                    member.option
                                )
                            ) {
                                return true
                            } else {
                                return false
                            }
                        })
                        if (orgUserFromTag) {
                            itineraryUserIds.push(
                                ...orgUserFromTag.map((u) => u.id)
                            )
                        }
                    }
                }
                return users.filter((user) => {
                    return itineraryUserIds.includes(user.id)
                })
            } else {
                /// No members list
                return []
            }
        } catch (error) {
            spLog('Failed to get itinerary users', error)
            return []
        }
    }
)
